import React from 'react'
import { slugs } from '@/constants'
import { Translate, TranslateFunction } from '@/utils/translate/translate-client'

export type GiveawayFaq = {
  key: string
  question: (t: TranslateFunction) => string
  answer: (t: TranslateFunction) => string | React.JSX.Element
}

const faqs = {
  [slugs.homestead]: [
    {
      key: 'homesteadReleaseDate',
      question: (t: TranslateFunction) =>
        t('homesteadReleaseDateQuestion', 'When will the movie Homestead be in theaters?'),
      answer: (t: TranslateFunction) => (
        <span className="!font-[bio-sans] !text-homestead-blue">
          {t('homesteadReleaseDateAnswer', 'Homestead will open in theaters on December 20th, 2024.')}
        </span>
      ),
    },
    {
      key: 'howPreOrdersWork',
      question: (t: TranslateFunction) => t('howPreOrdersWorkQuestion', 'How do pre-orders work?'),
      answer: (t: TranslateFunction) => (
        <Translate t={t} i18nKey="howPreOrdersWorkAnswer">
          <ul className="flex list-inside list-disc flex-col gap-2 !font-[bio-sans] !text-homestead-blue">
            <li>
              Your ticket codes will be delivered via email (check your spam/junk folder if you don&apos;t receive them
              within 10-15 minutes after ordering).
            </li>
            <li>
              Ticket codes are valid for the specific ticket type purchased and can be redeemed during the theatrical
              run at participating theaters listed on{' '}
              <a className="underline" target="_blank" href="https://angel.com/tickets">
                angel.com/tickets
              </a>
              .
            </li>
            <li>
              Once showtimes become available, ticket codes can ONLY be redeemed at{' '}
              <a className="underline" target="_blank" href="https://angel.com/tickets">
                angel.com/tickets
              </a>
              .
            </li>
            <li>
              Pre-orders are 100% refundable up to 120 days after purchase of your ticket codes. For additional terms,
              please visit{' '}
              <a className="underline" target="_blank" href="https://angel.com/terms">
                angel.com/terms
              </a>
              .
            </li>
          </ul>
        </Translate>
      ),
    },
    {
      key: 'refundPolicy',
      question: (t: TranslateFunction) =>
        t(
          'refundPolicyHomesteadGiveawayQuestion',
          'What are the refund and expiration policies for ticket packages with sweepstakes entries?',
        ),
      answer: (t: TranslateFunction) => (
        <Translate t={t} i18nKey="refundPolicyHomesteadGiveawayAnswerV2">
          <span className="!font-[bio-sans] !text-homestead-blue">
            You can request a full refund within 120 days of purchase or receive store credit after that, up until six
            weeks after the movie release date. For packages that include perks, refunds are available until any perk is
            used. Similarly, for tickets or packages that include sweepstakes entries, refunds are possible until
            winners are selected or other specified conditions are met. Please refer to the official rules for exact
            dates and details. Refunds are available when no showtimes are within 50 miles of your home. To request a
            refund, please{' '}
            <a
              className="underline"
              target="_blank"
              href="https://support.angel.com/hc/en-us/requests/new?tf_20897151291671=yes_ticket_purcahsed_field&ticket_form_id=20949720698391"
            >
              click here
            </a>
            .
          </span>
        </Translate>
      ),
    },
    {
      key: 'giveawayDates',
      question: (t: TranslateFunction) =>
        t('giveawayDatesQuestion', 'When does the Giveaway begin and end and winners announced?'),
      answer: (t: TranslateFunction) => (
        <span className="!font-[bio-sans] !text-homestead-blue">
          {t(
            'giveawayDatesAnswer',
            'The Promotion begins at 12:00 a.m. Mountain Time ("MT") on October 7, 2024 and ends at 11:59 p.m. MT on December 22, 2024 (the "Promotion Period"). The Grand Prize Drawing will take place on or about January 14, 2025. Visit the Official Rules linked in the footer of the website for more information.',
          )}
        </span>
      ),
    },
    {
      key: 'giveawayInformation',
      question: (t: TranslateFunction) =>
        t(
          'giveawayInformationQuestion',
          'How can I get more information about the Giveaway dates, eligibility, prizes or other information?',
        ),
      answer: (t: TranslateFunction) => (
        <span className="!font-[bio-sans] !text-homestead-blue">
          {t(
            'giveawayInformationAnswer',
            'The 300K Instant Homestead Giveaway sweepstakes is administered by SweepstakesPros, a leading promotion administrator for over 30 years. Visit the Official Rules linked in the footer of the website.',
          )}
        </span>
      ),
    },
    {
      key: 'howToReceiveBenefits',
      question: (t: TranslateFunction) =>
        t('howToReceiveBenefitsQuestion', 'How can I receive my pre-order tickets, entries, and superfan benefits?'),
      answer: (t: TranslateFunction) => (
        <span className="!font-[bio-sans] !text-homestead-blue">
          {t(
            'howToReceiveBenefitsAnswer',
            'Your pre-order ticket codes will be emailed to you upon completing your order. If you have an Angel account, you can view your ticket codes by logging into the email account you used to purchase your tickets on angel.com, go to My Account, and then go to My Tickets. You can also access your tickets by checking More/Tickets in the Angel Studios mobile app.',
          )}
        </span>
      ),
    },
    {
      key: 'showtimesAvailable',
      question: (t: TranslateFunction) =>
        t('showtimesAvailableQuestion', 'When will showtimes be available for me to choose my seats?'),
      answer: (t: TranslateFunction) => (
        <Translate t={t} i18nKey="showtimesAvailableAnswer">
          <span className="!font-[bio-sans] !text-homestead-blue">
            Showtimes are typically available six weeks before the release date. For Homestead, they may be available as
            early as Friday, November 8th, 2024. These dates may fluctuate, but once showtimes are ready, we&apos;ll
            notify you via email, as well as through notifications on angel.com and our mobile apps. Tickets can be
            booked, when showtimes are available, at{' '}
            <a className="underline" target="_blank" href="https://angel.com/tickets">
              angel.com/tickets
            </a>
            .
          </span>
        </Translate>
      ),
    },
    {
      key: 'canIShareTickets',
      question: (t: TranslateFunction) =>
        t('canIShareTicketsQuestion', 'Can I share my pre-order tickets with others?'),
      answer: (t: TranslateFunction) => (
        <Translate t={t} i18nKey="canIShareTicketsAnswer">
          <span className="!font-[bio-sans] !text-homestead-blue">
            Yes! Your pre-order tickets come as promo codes. You can share these codes with anyone, and they can redeem
            them for showtimes when they become available, exclusively at{' '}
            <a className="underline" target="_blank" href="https://angel.com/tickets">
              angel.com/tickets
            </a>
          </span>
        </Translate>
      ),
    },
    {
      key: 'canUseAcrossMultipleMovies',
      question: (t: TranslateFunction) =>
        t('canUseAcrossMultipleMoviesQuestion', 'Can I use these pre-order tickets for another Angel Studios movie?'),
      answer: (t: TranslateFunction) => (
        <Translate t={t} i18nKey="canUseAcrossMultipleMoviesAnswer">
          <span className="!font-[bio-sans] !text-homestead-blue">
            No, these pre-order tickets are valid only for Homestead and can be redeemed only at{' '}
            <a className="underline" target="_blank" href="https://angel.com/tickets">
              angel.com/tickets
            </a>
            .
          </span>
        </Translate>
      ),
    },
    {
      key: 'travelCovered',
      question: (t: TranslateFunction) =>
        t('travelCoveredQuestion', 'If I get the two red carpet movie premiere tickets, will travel be covered?'),
      answer: (t: TranslateFunction) => (
        <span className="!font-[bio-sans] !text-homestead-blue">
          {t(
            'travelCoveredAnswer',
            'No. Travel, lodging, and food expenses are not covered by the superfan benefit. This benefit only covers access for two to the red carpet event Homestead. Any additional costs are the responsibility of the recipient',
          )}
        </span>
      ),
    },
    {
      key: 'howToContactSupport',
      question: (t: TranslateFunction) =>
        t('howToContactSupportQuestion', 'How can I contact support if I have issues with my pre-order?'),
      answer: (t: TranslateFunction) => (
        <Translate t={t} i18nKey="howToContactSupportAnswer">
          <span className="!font-[bio-sans] !text-homestead-blue">
            If you need assistance with your pre-order, simply click{' '}
            <a className="underline" target="_blank" href="https://angel.com/help">
              here
            </a>{' '}
            to contact our support team at Angel Studios.
          </span>
        </Translate>
      ),
    },
  ],
  [slugs.braveTheDark]: [
    {
      key: 'braveReleaseDate',
      question: (t: TranslateFunction) =>
        t('braveReleaseDateQuestion', 'When will the movie Brave The Dark be in theaters?'),
      answer: (t: TranslateFunction) => (
        <span className="!font-[bio-sans] !text-homestead-blue">
          {t('braveReleaseDateAnswer', 'Brave The Dark will open in theaters on January 24, 2025')}
        </span>
      ),
    },
    {
      key: 'braveHowPreOrdersWork',
      question: (t: TranslateFunction) => t('howPreOrdersWorkQuestionBrave', 'How do pre-orders work?'),
      answer: (t: TranslateFunction) => (
        <Translate t={t} i18nKey="howPreOrdersWorkAnswerBrave">
          <ul className="flex list-inside list-disc flex-col gap-2 !font-[bio-sans] !text-homestead-blue">
            <li>
              Your ticket codes will be delivered via email (check your spam/junk folder if you don&apos;t receive them
              within 10-15 minutes after ordering).
            </li>
            <li>
              Ticket codes are valid for the specific ticket type purchased and can be redeemed during the theatrical
              run at participating theaters listed on{' '}
              <a className="underline" target="_blank" href="https://angel.com/tickets">
                angel.com/tickets
              </a>
              .
            </li>
            <li>
              Once showtimes become available, ticket codes can ONLY be redeemed at{' '}
              <a className="underline" target="_blank" href="https://angel.com/tickets">
                angel.com/tickets
              </a>
              .
            </li>
            <li>
              Pre-orders are 100% refundable up to 120 days after purchase of your ticket codes. For additional terms,
              please visit{' '}
              <a className="underline" target="_blank" href="https://angel.com/terms">
                angel.com/terms
              </a>
              .
            </li>
          </ul>
        </Translate>
      ),
    },
    {
      key: 'braveWhenDoesGiveawayEnd',
      question: (t: TranslateFunction) =>
        t('whenDoesGiveawayEndBrave', 'When does the Giveaway begin, and end, and winners announced? '),
      answer: (t: TranslateFunction) => (
        <Translate t={t} i18nKey="whenDoesGiveawayEndDescriptionBrave">
          The Promotion begins at 12:00 a.m. Mountain Time (&quot;MT&quot;) on December 4, 2024 and ends at 11:59 p.m.
          MT on March 9, 2024 (the &quot;Promotion Period&quot;). The Grand Prize Drawing will take place on or about
          March 31, 2025. Visit the Official Rules linked in the footer of the website for more information.
        </Translate>
      ),
    },
    {
      key: 'braveGiveawayInformation',
      question: (t: TranslateFunction) =>
        t(
          'giveawayInformationQuestionBrave',
          'How can I get more information about the Giveaway dates, eligibility, prizes or other information?',
        ),
      answer: (t: TranslateFunction) => (
        <span className="!font-[bio-sans] !text-homestead-blue">
          {t(
            'giveawayInformationAnswerBrave',
            'The 200K Dream Vacation Giveaway sweepstakes is administered by SweepstakesPros, a leading promotion administrator for over 30 years. Visit the Official Rules linked in the footer of the website. ',
          )}
        </span>
      ),
    },
    {
      key: 'braveHowToReceiveBenefits',
      question: (t: TranslateFunction) =>
        t(
          'howToReceiveBenefitsQuestionBrave',
          'How can I receive my pre-order tickets, entries, and superfan benefits?',
        ),
      answer: (t: TranslateFunction) => (
        <span className="!font-[bio-sans] !text-homestead-blue">
          {t(
            'howToReceiveBenefitsAnswerBrave',
            'Your pre-order ticket codes will be emailed to you upon completing your order. If you have an Angel account, you can view your ticket codes by logging into the email account you used to purchase your tickets on angel.com, go to My Account, and then go to My Tickets. You can also access your tickets by checking More/Tickets in the Angel Studios mobile app.',
          )}
        </span>
      ),
    },
    {
      key: 'braveShowtimesAvailable',
      question: (t: TranslateFunction) =>
        t('showtimesAvailableQuestionBrave', 'When will showtimes be available for me to choose my seats?'),
      answer: (t: TranslateFunction) => (
        <Translate t={t} i18nKey="showtimesAvailableAnswerBraveV2">
          <span className="!font-[bio-sans] !text-homestead-blue">
            Showtimes are typically available six weeks before the release date. For Brave The Dark, they may be
            available as early as Wednesday, December 4th, 2024. These dates may fluctuate, but once showtimes are
            ready, we&apos;ll notify you via email, as well as through notifications on angel.com and our mobile apps.
            Tickets can be booked, when showtimes are available, at{' '}
            <a className="underline" target="_blank" href="https://angel.com/tickets">
              angel.com/tickets
            </a>
            .
          </span>
        </Translate>
      ),
    },
    {
      key: 'braveCanIShareTickets',
      question: (t: TranslateFunction) =>
        t('canIShareTicketsQuestionBrave', 'Can I share my pre-order tickets with others?'),
      answer: (t: TranslateFunction) => (
        <Translate t={t} i18nKey="canIShareTicketsAnswerBrave">
          <span className="!font-[bio-sans] !text-homestead-blue">
            Yes! Your pre-order tickets come as promo codes. You can share these codes with anyone, and they can redeem
            them for showtimes when they become available, exclusively at{' '}
            <a className="underline" target="_blank" href="https://angel.com/tickets">
              angel.com/tickets
            </a>
          </span>
        </Translate>
      ),
    },
    {
      key: 'braveCanUseAcrossMultipleMovies',
      question: (t: TranslateFunction) =>
        t(
          'canUseAcrossMultipleMoviesQuestionBrave',
          'Can I use these pre-order tickets for another Angel Studios movie?',
        ),
      answer: (t: TranslateFunction) => (
        <Translate t={t} i18nKey="canUseAcrossMultipleMoviesAnswerV2">
          <span className="!font-[bio-sans] !text-homestead-blue">
            No, these pre-order tickets are valid only for Brave the Dark and can be redeemed only at{' '}
            <a className="underline" target="_blank" href="https://angel.com/tickets">
              angel.com/tickets
            </a>
            .
          </span>
        </Translate>
      ),
    },
    {
      key: 'braveHowToContactSupport',
      question: (t: TranslateFunction) =>
        t('howToContactSupportQuestionBrave', 'How can I contact support if I have issues with my pre-order?'),
      answer: (t: TranslateFunction) => (
        <Translate t={t} i18nKey="howToContactSupportAnswer">
          <span className="!font-[bio-sans] !text-homestead-blue">
            If you need assistance with your pre-order, simply click{' '}
            <a className="underline" target="_blank" href="https://angel.com/help">
              here
            </a>{' '}
            to contact our support team at Angel Studios.
          </span>
        </Translate>
      ),
    },
  ],

  [slugs.ruleBreakers]: [
    {
      key: 'ruleBreakersReleaseDate',
      question: (t: TranslateFunction) =>
        t('ruleBreakersReleaseDateQuestion', 'When will the movie Rule Breakers be in theaters?'),
      answer: (t: TranslateFunction) => (
        <span className="!font-[bio-sans] !text-homestead-blue">
          {t('ruleBreakersReleaseDateAnswer', 'Rule Breakers will open in theaters on March 7, 2024.')}
        </span>
      ),
    },
    {
      key: 'ruleBreakersHowPreOrdersWork',
      question: (t: TranslateFunction) => t('howPreOrdersWorkQuestionRule', 'How do pre-orders work?'),
      answer: (t: TranslateFunction) => (
        <Translate t={t} i18nKey="howPreOrdersWorkAnswerRule">
          <ul className="flex list-inside list-disc flex-col gap-2 !font-[bio-sans] !text-homestead-blue">
            <li>
              Your ticket codes will be delivered via email (check your spam/junk folder if you don&apos;t receive them
              within 10-15 minutes after ordering).
            </li>
            <li>
              Ticket codes are valid for the specific ticket type purchased and can be redeemed during the theatrical
              run at participating theaters listed on{' '}
              <a className="underline" target="_blank" href="https://angel.com/tickets">
                angel.com/tickets
              </a>
              .
            </li>
            <li>
              Once showtimes become available, ticket codes can ONLY be redeemed at{' '}
              <a className="underline" target="_blank" href="https://angel.com/tickets">
                angel.com/tickets
              </a>
              .
            </li>
            <li>
              Pre-orders are 100% refundable up to 120 days after purchase of your ticket codes. For additional terms,
              please visit{' '}
              <a className="underline" target="_blank" href="https://angel.com/terms">
                angel.com/terms
              </a>
              .
            </li>
          </ul>
        </Translate>
      ),
    },
    {
      key: 'ruleBreakersWhenDoesGiveawayEnd',
      question: (t: TranslateFunction) =>
        t('whenDoesGiveawayEndRule', 'When does the Giveaway begin, and end, and winners announced? '),
      answer: (t: TranslateFunction) => (
        <Translate t={t} i18nKey="whenDoesGiveawayEndDescriptionRule">
          The Promotion begins at 12:00 a.m. Mountain Time (&quot;MT&quot;) on December 4, 2024 and ends at 11:59 p.m.
          MT on March 9, 2024 (the &quot;Promotion Period&quot;). The Grand Prize Drawing will take place on or about
          March 31, 2025. Visit the Official Rules linked in the footer of the website for more information.
        </Translate>
      ),
    },
    {
      key: 'ruleBreakersGiveawayInformation',
      question: (t: TranslateFunction) =>
        t(
          'giveawayInformationQuestionRule',
          'How can I get more information about the Giveaway dates, eligibility, prizes or other information?',
        ),
      answer: (t: TranslateFunction) => (
        <span className="!font-[bio-sans] !text-homestead-blue">
          {t(
            'giveawayInformationAnswerRule',
            'The 200K Dream Vacation Giveaway sweepstakes is administered by SweepstakesPros, a leading promotion administrator for over 30 years. Visit the Official Rules linked in the footer of the website. ',
          )}
        </span>
      ),
    },
    {
      key: 'ruleBreakersHowToReceiveBenefits',
      question: (t: TranslateFunction) =>
        t(
          'howToReceiveBenefitsQuestionRule',
          'How can I receive my pre-order tickets, entries, and superfan benefits?',
        ),
      answer: (t: TranslateFunction) => (
        <span className="!font-[bio-sans] !text-homestead-blue">
          {t(
            'howToReceiveBenefitsAnswerRule',
            'Your pre-order ticket codes will be emailed to you upon completing your order. If you have an Angel account, you can view your ticket codes by logging into the email account you used to purchase your tickets on angel.com, go to My Account, and then go to My Tickets. You can also access your tickets by checking More/Tickets in the Angel Studios mobile app.',
          )}
        </span>
      ),
    },
    {
      key: 'ruleBreakersShowtimesAvailable',
      question: (t: TranslateFunction) =>
        t('showtimesAvailableQuestionRule', 'When will showtimes be available for me to choose my seats?'),
      answer: (t: TranslateFunction) => (
        <Translate t={t} i18nKey="showtimesAvailableAnswerRuleV3">
          <span className="!font-[bio-sans] !text-homestead-blue">
            Showtimes are typically available six weeks before the release date. For Rule Breakers, they may be
            available as early as Tuesday, January 21st, 2025. These dates may fluctuate, but once showtimes are ready,
            we&apos;ll notify you via email, as well as through notifications on angel.com and our mobile apps. Tickets
            can be booked, when showtimes are available, at{' '}
            <a className="underline" target="_blank" href="https://angel.com/tickets">
              angel.com/tickets
            </a>
            .
          </span>
        </Translate>
      ),
    },
    {
      key: 'ruleBreakersCanIShareTickets',
      question: (t: TranslateFunction) =>
        t('canIShareTicketsQuestionRule', 'Can I share my pre-order tickets with others?'),
      answer: (t: TranslateFunction) => (
        <Translate t={t} i18nKey="canIShareTicketsAnswerRule">
          <span className="!font-[bio-sans] !text-homestead-blue">
            Yes! Your pre-order tickets come as promo codes. You can share these codes with anyone, and they can redeem
            them for showtimes when they become available, exclusively at{' '}
            <a className="underline" target="_blank" href="https://angel.com/tickets">
              angel.com/tickets
            </a>
          </span>
        </Translate>
      ),
    },
    {
      key: 'ruleBreakersCanUseAcrossMultipleMovies',
      question: (t: TranslateFunction) =>
        t(
          'canUseAcrossMultipleMoviesQuestionRule',
          'Can I use these pre-order tickets for another Angel Studios movie?',
        ),
      answer: (t: TranslateFunction) => (
        <Translate t={t} i18nKey="canUseAcrossMultipleMoviesAnswerRule">
          <span className="!font-[bio-sans] !text-homestead-blue">
            No, these pre-order tickets are valid only for Rule Breakers and can be redeemed only at{' '}
            <a className="underline" target="_blank" href="https://angel.com/tickets">
              angel.com/tickets
            </a>
            .
          </span>
        </Translate>
      ),
    },
    {
      key: 'ruleBreakersHowToContactSupport',
      question: (t: TranslateFunction) =>
        t('howToContactSupportQuestionRuleBreakers', 'How can I contact support if I have issues with my pre-order?'),
      answer: (t: TranslateFunction) => (
        <Translate t={t} i18nKey="howToContactSupportAnswerRule">
          <span className="!font-[bio-sans] !text-homestead-blue">
            If you need assistance with your pre-order, simply click{' '}
            <a className="underline" target="_blank" href="https://angel.com/help">
              here
            </a>{' '}
            to contact our support team at Angel Studios.
          </span>
        </Translate>
      ),
    },
  ],

  ['rule-breakers-tech']: [
    {
      key: 'ruleBreakersReleaseDate',
      question: (t: TranslateFunction) =>
        t('ruleBreakersReleaseDateQuestion', 'When will the movie Rule Breakers be in theaters?'),
      answer: (t: TranslateFunction) => (
        <span className="!font-[bio-sans] !text-homestead-blue">
          {t('ruleBreakersReleaseDateAnswer', 'Rule Breakers will open in theaters on March 7, 2024.')}
        </span>
      ),
    },
    {
      key: 'ruleBreakersHowPreOrdersWork',
      question: (t: TranslateFunction) => t('howPreOrdersWorkQuestionRule', 'How do pre-orders work?'),
      answer: (t: TranslateFunction) => (
        <Translate t={t} i18nKey="howPreOrdersWorkAnswerRule">
          <ul className="flex list-inside list-disc flex-col gap-2 !font-[bio-sans] !text-homestead-blue">
            <li>
              Your ticket codes will be delivered via email (check your spam/junk folder if you don&apos;t receive them
              within 10-15 minutes after ordering).
            </li>
            <li>
              Ticket codes are valid for the specific ticket type purchased and can be redeemed during the theatrical
              run at participating theaters listed on{' '}
              <a className="underline" target="_blank" href="https://angel.com/tickets">
                angel.com/tickets
              </a>
              .
            </li>
            <li>
              Once showtimes become available, ticket codes can ONLY be redeemed at{' '}
              <a className="underline" target="_blank" href="https://angel.com/tickets">
                angel.com/tickets
              </a>
              .
            </li>
            <li>
              Pre-orders are 100% refundable up to 120 days after purchase of your ticket codes. For additional terms,
              please visit{' '}
              <a className="underline" target="_blank" href="https://angel.com/terms">
                angel.com/terms
              </a>
              .
            </li>
          </ul>
        </Translate>
      ),
    },
    {
      key: 'ruleBreakersGiveawayInformation',
      question: (t: TranslateFunction) =>
        t(
          'giveawayInformationQuestionRule',
          'How can I get more information about the Giveaway dates, eligibility, prizes or other information?',
        ),
      answer: (t: TranslateFunction) => (
        <span className="!font-[bio-sans] !text-homestead-blue">
          {t(
            'giveawayInformationAnswerRuleV2',
            'The Tecg Giveaway sweepstakes is administered by SweepstakesPros, a leading promotion administrator for over 30 years. Visit the Official Rules linked in the footer of the website. ',
          )}
        </span>
      ),
    },
    {
      key: 'ruleBreakersHowToReceiveBenefits',
      question: (t: TranslateFunction) =>
        t(
          'howToReceiveBenefitsQuestionRule',
          'How can I receive my pre-order tickets, entries, and superfan benefits?',
        ),
      answer: (t: TranslateFunction) => (
        <span className="!font-[bio-sans] !text-homestead-blue">
          {t(
            'howToReceiveBenefitsAnswerRule',
            'Your pre-order ticket codes will be emailed to you upon completing your order. If you have an Angel account, you can view your ticket codes by logging into the email account you used to purchase your tickets on angel.com, go to My Account, and then go to My Tickets. You can also access your tickets by checking More/Tickets in the Angel Studios mobile app.',
          )}
        </span>
      ),
    },
    {
      key: 'ruleBreakersShowtimesAvailable',
      question: (t: TranslateFunction) =>
        t('showtimesAvailableQuestionRule', 'When will showtimes be available for me to choose my seats?'),
      answer: (t: TranslateFunction) => (
        <Translate t={t} i18nKey="showtimesAvailableAnswerRuleV3">
          <span className="!font-[bio-sans] !text-homestead-blue">
            Showtimes are typically available six weeks before the release date. For Rule Breakers, they may be
            available as early as Tuesday, January 21st, 2025. These dates may fluctuate, but once showtimes are ready,
            we&apos;ll notify you via email, as well as through notifications on angel.com and our mobile apps. Tickets
            can be booked, when showtimes are available, at{' '}
            <a className="underline" target="_blank" href="https://angel.com/tickets">
              angel.com/tickets
            </a>
            .
          </span>
        </Translate>
      ),
    },
    {
      key: 'ruleBreakersCanIShareTickets',
      question: (t: TranslateFunction) =>
        t('canIShareTicketsQuestionRule', 'Can I share my pre-order tickets with others?'),
      answer: (t: TranslateFunction) => (
        <Translate t={t} i18nKey="canIShareTicketsAnswerRule">
          <span className="!font-[bio-sans] !text-homestead-blue">
            Yes! Your pre-order tickets come as promo codes. You can share these codes with anyone, and they can redeem
            them for showtimes when they become available, exclusively at{' '}
            <a className="underline" target="_blank" href="https://angel.com/tickets">
              angel.com/tickets
            </a>
          </span>
        </Translate>
      ),
    },
    {
      key: 'ruleBreakersCanUseAcrossMultipleMovies',
      question: (t: TranslateFunction) =>
        t(
          'canUseAcrossMultipleMoviesQuestionRule',
          'Can I use these pre-order tickets for another Angel Studios movie?',
        ),
      answer: (t: TranslateFunction) => (
        <Translate t={t} i18nKey="canUseAcrossMultipleMoviesAnswerRule">
          <span className="!font-[bio-sans] !text-homestead-blue">
            No, these pre-order tickets are valid only for Rule Breakers and can be redeemed only at{' '}
            <a className="underline" target="_blank" href="https://angel.com/tickets">
              angel.com/tickets
            </a>
            .
          </span>
        </Translate>
      ),
    },
    {
      key: 'ruleBreakersHowToContactSupport',
      question: (t: TranslateFunction) =>
        t('howToContactSupportQuestionRuleBreakers', 'How can I contact support if I have issues with my pre-order?'),
      answer: (t: TranslateFunction) => (
        <Translate t={t} i18nKey="howToContactSupportAnswerRule">
          <span className="!font-[bio-sans] !text-homestead-blue">
            If you need assistance with your pre-order, simply click{' '}
            <a className="underline" target="_blank" href="https://angel.com/help">
              here
            </a>{' '}
            to contact our support team at Angel Studios.
          </span>
        </Translate>
      ),
    },
  ],

  [slugs.kingOfKings]: [
    {
      key: 'kingOfKingsReleaseDate',
      question: (t: TranslateFunction) =>
        t('kingOfKingsReleaseDateQuestion', 'When will the movie The King of Kings be in theaters?'),
      answer: (t: TranslateFunction) => (
        <span className="!font-[bio-sans] !text-homestead-blue">
          {t('kingOfKingsReleaseDateAnswer', 'The King of Kings will open in theaters on April 11, 2025.')}
        </span>
      ),
    },
    {
      key: 'kingOfKingsHowPreOrdersWork',
      question: (t: TranslateFunction) => t('howPreOrdersWorkQuestionKOK', 'How do pre-order tickets work?'),
      answer: (t: TranslateFunction) => (
        <Translate t={t} i18nKey="howPreOrdersWorkAnswerKOK">
          <ul className="flex list-inside list-disc flex-col gap-2 !font-[bio-sans] !text-homestead-blue">
            <li>
              Your ticket codes will be delivered via email (check your spam/junk folder if you don&apos;t receive them
              within 10-15 minutes after ordering).
            </li>
            <li>
              Ticket codes are valid for the specific ticket type purchased and can be redeemed during the theatrical
              run at participating theaters listed on{' '}
              <a className="underline" target="_blank" href="https://angel.com/tickets">
                angel.com/tickets
              </a>
              .
            </li>
            <li>
              Once showtimes become available, ticket codes can ONLY be redeemed at{' '}
              <a className="underline" target="_blank" href="https://angel.com/tickets">
                angel.com/tickets
              </a>
              .
            </li>
            <li>
              Pre-orders are 100% refundable up to 120 days after purchase of your ticket codes. For additional terms,
              please visit{' '}
              <a className="underline" target="_blank" href="https://angel.com/terms">
                angel.com/terms
              </a>
              .
            </li>
          </ul>
        </Translate>
      ),
    },
    {
      key: 'kingOfKingsGiveawayDates',
      question: (t: TranslateFunction) =>
        t('giveawayDatesQuestionKOK', 'When does the Giveaway begin and end, and when are winners announced?'),
      answer: (t: TranslateFunction) => (
        <span className="!font-[bio-sans] !text-homestead-blue">
          {t(
            'giveawayDatesAnswerKOK',
            'The Promotion begins at 12:00 a.m. Mountain Time ("MT") on February 7, 2025 and ends at 11:59 p.m. MT on April 27, 2025. The Grand Prize Drawing will take place on or about May 12, 2025. Visit the Official Rules linked in the footer of the website for more details.',
          )}
        </span>
      ),
    },
    {
      key: 'kingOfKingsGiveawayInformation',
      question: (t: TranslateFunction) =>
        t(
          'giveawayInformationQuestionKOK',
          'How can I get more information about the Giveaway dates, eligibility, prizes, or other information?',
        ),
      answer: (t: TranslateFunction) => (
        <span className="!font-[bio-sans] !text-homestead-blue">
          {t(
            'giveawayInformationAnswerKOK',
            'The King of Kings Giveaway is administered by SweepstakesPros, a leading promotion administrator with over 30 years of experience. For full details, visit the Official Rules linked in the footer of the website.',
          )}
        </span>
      ),
    },
    {
      key: 'kingOfKingsHowToReceiveBenefits',
      question: (t: TranslateFunction) =>
        t('howToReceiveBenefitsQuestionKOK', 'How can I receive my pre-order tickets, entries, and superfan benefits?'),
      answer: (t: TranslateFunction) => (
        <span className="!font-[bio-sans] !text-homestead-blue">
          {t(
            'howToReceiveBenefitsAnswerKOK',
            'Your pre-order ticket codes will be emailed to you upon completing your order. If you have an Angel account, you can view your ticket codes by logging into the email account you used to purchase tickets on angel.com, navigating to My Account > My Tickets. You can also access your tickets through the Angel Studios mobile app under More > Tickets.',
          )}
        </span>
      ),
    },
    {
      key: 'kingOfKingsShowtimesAvailable',
      question: (t: TranslateFunction) =>
        t('showtimesAvailableQuestionKOK', 'When will showtimes be available for me to choose my seats?'),
      answer: (t: TranslateFunction) => (
        <Translate t={t} i18nKey="showtimesAvailableAnswerKOKV2">
          <span className="!font-[bio-sans] !text-homestead-blue">
            Showtimes are typically available six weeks before the release date. For The King of Kings, they may be
            available as early as Wednesday, February 4, 2025. These dates may fluctuate, but once showtimes are ready,
            we&apos;ll notify you via email, as well as through notifications on angel.com and our mobile apps. Tickets
            can be booked, when showtimes are available, at{' '}
            <a className="underline" target="_blank" href="https://angel.com/tickets">
              angel.com/tickets
            </a>
            .
          </span>
        </Translate>
      ),
    },
    {
      key: 'kingOfKingsMatineeVsPrimetime',
      question: (t: TranslateFunction) =>
        t('matineeVsPrimetimeQuestionKOK', 'What are Matinee tickets vs. Primetime tickets?'),
      answer: (t: TranslateFunction) => (
        <Translate t={t} i18nKey="matineeVsPrimetimeAnswerKOK">
          <ul className="flex list-inside list-disc flex-col gap-2 !font-[bio-sans] !text-homestead-blue">
            <li>
              Matinee Tickets: Redeemable for any showtimes before 4:00 p.m. on non-holiday dates.
              <ul className="ml-6 mt-2">
                <li>Holidays where matinee tickets are not valid:</li>
                <li>November 28, December 24-25, December 31, and January 1.</li>
                <li>Matinee tickets are valid on December 26 and December 30 before 4:00 p.m.</li>
              </ul>
            </li>
            <li>
              Primetime Tickets: Provide the best chance to reserve your preferred seats.
              <ul className="ml-6 mt-2">
                <li>Valid any day or time, including holidays.</li>
                <li>You can upgrade to Primetime Tickets during checkout or through promotional emails.</li>
              </ul>
            </li>
          </ul>
        </Translate>
      ),
    },
    {
      key: 'kingOfKingsRefundPolicy',
      question: (t: TranslateFunction) =>
        t(
          'refundPolicyQuestionKOK',
          'What are the refund and expiration policies for ticket packages with sweepstakes entries?',
        ),
      answer: (t: TranslateFunction) => (
        <Translate t={t} i18nKey="refundPolicyAnswerKOK">
          <span className="!font-[bio-sans] !text-homestead-blue">
            Full refunds are available within 120 days of purchase. After 120 days, you may receive store credit up
            until six weeks after the movie release date. For packages with perks or sweepstakes entries: if a perk has
            been used, refunds are no longer available. If a package includes sweepstakes entries, refunds are only
            possible until winners are selected or other specified conditions are met. See the Official Rules for exact
            dates and conditions.
          </span>
        </Translate>
      ),
    },
    {
      key: 'kingOfKingsCanIShareTickets',
      question: (t: TranslateFunction) =>
        t('canIShareTicketsQuestionKOK', 'Can I share my pre-order tickets with others?'),
      answer: (t: TranslateFunction) => (
        <Translate t={t} i18nKey="canIShareTicketsAnswerKOK">
          <span className="!font-[bio-sans] !text-homestead-blue">
            Yes! Your pre-order tickets come as promo codes, which you can share with anyone. They can redeem these
            codes for showtimes when available, exclusively at{' '}
            <a className="underline" target="_blank" href="https://angel.com/tickets">
              angel.com/tickets
            </a>
            .
          </span>
        </Translate>
      ),
    },
    {
      key: 'kingOfKingsCanUseAcrossMultipleMovies',
      question: (t: TranslateFunction) =>
        t(
          'canUseAcrossMultipleMoviesQuestionKOK',
          'Can I use these pre-order tickets for another Angel Studios movie?',
        ),
      answer: (t: TranslateFunction) => (
        <Translate t={t} i18nKey="canUseAcrossMultipleMoviesAnswerKOK">
          <span className="!font-[bio-sans] !text-homestead-blue">
            No. Pre-order tickets are valid only for The King of Kings and can only be redeemed at{' '}
            <a className="underline" target="_blank" href="https://angel.com/tickets">
              angel.com/tickets
            </a>
            .
          </span>
        </Translate>
      ),
    },
    {
      key: 'kingOfKingsHowToContactSupport',
      question: (t: TranslateFunction) =>
        t('howToContactSupportQuestionKOK', 'How can I contact support if I have issues with my pre-order?'),
      answer: (t: TranslateFunction) => (
        <Translate t={t} i18nKey="howToContactSupportAnswerKOK">
          <span className="!font-[bio-sans] !text-homestead-blue">
            If you need assistance with your pre-order, please click{' '}
            <a className="underline" target="_blank" href="https://angel.com/help">
              here
            </a>{' '}
            to contact our Angel Studios Support Team.
          </span>
        </Translate>
      ),
    },
  ],

  [slugs.theLastRodeo]: [
    {
      key: 'kingOfKingsReleaseDate',
      question: (t: TranslateFunction) =>
        t('lastRodeoReleaseDateQuestion', 'When will the movie The Last Rodeo be in theaters?'),
      answer: (t: TranslateFunction) => (
        <span className="!font-[bio-sans] !text-homestead-blue">
          {t('lastRodeoReleaseDateAnswer', 'The Last Rodeo will open in theaters on May 23rd, 2025.')}
        </span>
      ),
    },
    {
      key: 'kingOfKingsHowPreOrdersWork',
      question: (t: TranslateFunction) => t('howPreOrdersWorkQuestionKOK', 'How do pre-order tickets work?'),
      answer: (t: TranslateFunction) => (
        <Translate t={t} i18nKey="howPreOrdersWorkAnswerKOK">
          <ul className="flex list-inside list-disc flex-col gap-2 !font-[bio-sans] !text-homestead-blue">
            <li>
              Your ticket codes will be delivered via email (check your spam/junk folder if you don&apos;t receive them
              within 10-15 minutes after ordering).
            </li>
            <li>
              Ticket codes are valid for the specific ticket type purchased and can be redeemed during the theatrical
              run at participating theaters listed on{' '}
              <a className="underline" target="_blank" href="https://angel.com/tickets">
                angel.com/tickets
              </a>
              .
            </li>
            <li>
              Once showtimes become available, ticket codes can ONLY be redeemed at{' '}
              <a className="underline" target="_blank" href="https://angel.com/tickets">
                angel.com/tickets
              </a>
              .
            </li>
            <li>
              Pre-orders are 100% refundable up to 120 days after purchase of your ticket codes. For additional terms,
              please visit{' '}
              <a className="underline" target="_blank" href="https://angel.com/terms">
                angel.com/terms
              </a>
              .
            </li>
          </ul>
        </Translate>
      ),
    },
    {
      key: 'kingOfKingsGiveawayDates',
      question: (t: TranslateFunction) =>
        t('giveawayDatesQuestionKOK', 'When does the Giveaway begin and end, and when are winners announced?'),
      answer: (t: TranslateFunction) => (
        <span className="!font-[bio-sans] !text-homestead-blue">
          {t(
            'giveawayDatesAnswerRodeoV3',
            'The Promotion begins at 12:00 p.m. Mountain Time ("MT") on February 14, 2025 and ends at 11:59 p.m. MT on June 6, 2025. The Grand Prize Drawing will take place on or about June 27, 2025. Visit the Official Rules linked in the footer of the website for more details.',
          )}
        </span>
      ),
    },
    {
      key: 'kingOfKingsGiveawayInformation',
      question: (t: TranslateFunction) =>
        t(
          'giveawayInformationQuestionKOK',
          'How can I get more information about the Giveaway dates, eligibility, prizes, or other information?',
        ),
      answer: (t: TranslateFunction) => (
        <span className="!font-[bio-sans] !text-homestead-blue">
          {t(
            'giveawayInformationAnswerRodeo',
            'The Last Rodeo Giveaway is administered by SweepstakesPros, a leading promotion administrator with over 30 years of experience. For full details, visit the Official Rules linked in the footer of the website.',
          )}
        </span>
      ),
    },
    {
      key: 'kingOfKingsHowToReceiveBenefits',
      question: (t: TranslateFunction) =>
        t('howToReceiveBenefitsQuestionKOK', 'How can I receive my pre-order tickets, entries, and superfan benefits?'),
      answer: (t: TranslateFunction) => (
        <span className="!font-[bio-sans] !text-homestead-blue">
          {t(
            'howToReceiveBenefitsAnswerKOK',
            'Your pre-order ticket codes will be emailed to you upon completing your order. If you have an Angel account, you can view your ticket codes by logging into the email account you used to purchase tickets on angel.com, navigating to My Account > My Tickets. You can also access your tickets through the Angel Studios mobile app under More > Tickets.',
          )}
        </span>
      ),
    },
    {
      key: 'kingOfKingsShowtimesAvailable',
      question: (t: TranslateFunction) =>
        t('showtimesAvailableQuestionKOK', 'When will showtimes be available for me to choose my seats?'),
      answer: (t: TranslateFunction) => (
        <Translate t={t} i18nKey="showtimesAvailableAnswerRodeo">
          <span className="!font-[bio-sans] !text-homestead-blue">
            Showtimes are typically available six weeks before the release date. For The Last Rodeo, they may be
            available as early as Thursday, February 13, 2025. These dates may fluctuate, but once showtimes are ready,
            we&apos;ll notify you via email, as well as through notifications on angel.com and our mobile apps. Tickets
            can be booked, when showtimes are available, at{' '}
            <a className="underline" target="_blank" href="https://angel.com/tickets">
              angel.com/tickets
            </a>
            .
          </span>
        </Translate>
      ),
    },
    {
      key: 'kingOfKingsRefundPolicy',
      question: (t: TranslateFunction) =>
        t(
          'refundPolicyQuestionKOK',
          'What are the refund and expiration policies for ticket packages with sweepstakes entries?',
        ),
      answer: (t: TranslateFunction) => (
        <Translate t={t} i18nKey="refundPolicyAnswerKOK">
          <span className="!font-[bio-sans] !text-homestead-blue">
            Full refunds are available within 120 days of purchase. After 120 days, you may receive store credit up
            until six weeks after the movie release date. For packages with perks or sweepstakes entries: if a perk has
            been used, refunds are no longer available. If a package includes sweepstakes entries, refunds are only
            possible until winners are selected or other specified conditions are met. See the Official Rules for exact
            dates and conditions.
          </span>
        </Translate>
      ),
    },
    {
      key: 'kingOfKingsCanIShareTickets',
      question: (t: TranslateFunction) =>
        t('canIShareTicketsQuestionKOK', 'Can I share my pre-order tickets with others?'),
      answer: (t: TranslateFunction) => (
        <Translate t={t} i18nKey="canIShareTicketsAnswerKOK">
          <span className="!font-[bio-sans] !text-homestead-blue">
            Yes! Your pre-order tickets come as promo codes, which you can share with anyone. They can redeem these
            codes for showtimes when available, exclusively at{' '}
            <a className="underline" target="_blank" href="https://angel.com/tickets">
              angel.com/tickets
            </a>
            .
          </span>
        </Translate>
      ),
    },
    {
      key: 'kingOfKingsCanUseAcrossMultipleMovies',
      question: (t: TranslateFunction) =>
        t(
          'canUseAcrossMultipleMoviesQuestionKOK',
          'Can I use these pre-order tickets for another Angel Studios movie?',
        ),
      answer: (t: TranslateFunction) => (
        <Translate t={t} i18nKey="canUseAcrossMultipleMoviesAnswerRodeo">
          <span className="!font-[bio-sans] !text-homestead-blue">
            No. Pre-order tickets are valid only for The Last Rodeo and can only be redeemed at{' '}
            <a className="underline" target="_blank" href="https://angel.com/tickets">
              angel.com/tickets
            </a>
            .
          </span>
        </Translate>
      ),
    },
    {
      key: 'kingOfKingsHowToContactSupport',
      question: (t: TranslateFunction) =>
        t('howToContactSupportQuestionKOK', 'How can I contact support if I have issues with my pre-order?'),
      answer: (t: TranslateFunction) => (
        <Translate t={t} i18nKey="howToContactSupportAnswerKOK">
          <span className="!font-[bio-sans] !text-homestead-blue">
            If you need assistance with your pre-order, please click{' '}
            <a className="underline" target="_blank" href="https://angel.com/help">
              here
            </a>{' '}
            to contact our Angel Studios Support Team.
          </span>
        </Translate>
      ),
    },
  ],
}

export const useGiveawayFaqs = (theatricalSlug: string): GiveawayFaq[] => {
  return faqs?.[theatricalSlug]
}
